import { DateTime } from 'luxon'
import { isPast, isToday, isTomorrow, startOfDay } from 'date-fns'
import { getRelativeDueDateLabel } from './getRelativeDueDateLabel'

export const mergeDateWithTime = (date: Date, timeSource?: Date) => {
  const result = new Date(date)
  const source = timeSource ?? date
  result.setHours(source.getHours(), source.getMinutes(), 0, 0)
  return result
}

export const normalizeToMinute = (value: Date) => {
  const next = new Date(value)
  next.setSeconds(0, 0)
  return next
}

export type LocaleOptions = {
  /** BCP-47 tag, e.g. 'en-US' or 'en-GB'. Defaults to browser locale. */
  locale?: string
  /** When true, format times using 24-hour clock; when false, 12-hour. */
  is24Hour?: boolean
}

type FormatDueDateOptions = LocaleOptions & {
  includeRelative?: boolean
  isAllDay?: boolean
}

const timeOptions = (is24Hour?: boolean): Intl.DateTimeFormatOptions => ({
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: is24Hour ? 'h23' : 'h12',
})

const shortMonthDay = (
  date: Date,
  locale?: string,
  includeYear = false,
): string =>
  date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
  })

const shortTime = (date: Date, options: LocaleOptions): string =>
  date.toLocaleTimeString(options.locale, timeOptions(options.is24Hour))

export const formatDueDateForInput = (
  value?: Date,
  options: FormatDueDateOptions = {},
) => {
  if (!value) {
    return ''
  }

  const dateTime = DateTime.fromJSDate(value)
  const now = DateTime.now()
  const includeRelative = options.includeRelative ?? true

  const includeYear = !dateTime.hasSame(now, 'year')
  const datePart = shortMonthDay(value, options.locale, includeYear)

  // Prefer the explicit isAllDay flag when provided; otherwise fall back to
  // the legacy 23:59 sentinel detection so existing callers keep working.
  const hasTime =
    options.isAllDay === true
      ? false
      : (dateTime.hour !== 0 || dateTime.minute !== 0) &&
        !(dateTime.hour === 23 && dateTime.minute === 59)

  const timeSuffix = hasTime ? ` at ${shortTime(value, options)}` : ''
  const absoluteLabel = `${datePart}${timeSuffix}`
  const relativeLabel = getRelativeDueDateLabel(dateTime, now)

  if (includeRelative && relativeLabel) {
    return `${relativeLabel} - ${absoluteLabel}`
  }

  return absoluteLabel
}

/**
 * Format a date range label with smart handling for all-day events and durations.
 * Accepts locale/is24Hour options so callers can honour the current user's
 * preferences; defaults to the browser locale and 12-hour clock when unset.
 */
export const formatDateRangeLabel = (
  startDate?: Date | null,
  dueDate?: Date | null,
  isAllDay?: boolean,
  options: LocaleOptions = {},
): string => {
  if (startDate && dueDate) {
    // Prefer explicit isAllDay flag; fall back to legacy time-based detection
    // (00:00 - 23:59) so existing callers continue to work.
    const isTimesAllDay =
      isAllDay ??
      (startDate.getHours() === 0 &&
        startDate.getMinutes() === 0 &&
        dueDate.getHours() === 23 &&
        dueDate.getMinutes() === 59)

    // Check if same day
    const sameDay =
      startDate.getFullYear() === dueDate.getFullYear() &&
      startDate.getMonth() === dueDate.getMonth() &&
      startDate.getDate() === dueDate.getDate()

    if (sameDay) {
      const datePart = shortMonthDay(startDate, options.locale)

      const isMidnightRange =
        startDate.getHours() === 0 &&
        startDate.getMinutes() === 0 &&
        dueDate.getHours() === 0 &&
        dueDate.getMinutes() === 0

      // If all-day (based on actual times) OR both times untouched at midnight, just show the date
      if (isTimesAllDay || isMidnightRange) {
        return datePart
      }

      const startTime = shortTime(startDate, options)
      const dueTime = shortTime(dueDate, options)

      // If times are identical, show a single time instead of a range dash
      if (
        startDate.getHours() === dueDate.getHours() &&
        startDate.getMinutes() === dueDate.getMinutes()
      ) {
        return `${datePart}, ${startTime}`
      }

      // Same day with time - show "MMM d, h:mm - h:mm a"
      return `${datePart}, ${startTime} - ${dueTime}`
    }

    // Different days
    const startPart = shortMonthDay(startDate, options.locale)
    const duePart = shortMonthDay(dueDate, options.locale)

    // A range reads "Sep 28 → Oct 2" (design 31b). If all-day, dates only.
    if (isTimesAllDay) {
      return `${startPart} → ${duePart}`
    }

    // Multi-day with times - an end left at midnight carries no real time,
    // so show just its date (e.g. "Sep 27 → Oct 11 5:00 PM").
    const withTime = (date: Date, datePart: string) =>
      isMidnight(date) ? datePart : `${datePart} ${shortTime(date, options)}`
    return `${withTime(startDate, startPart)} → ${withTime(dueDate, duePart)}`
  }

  if (dueDate) {
    return formatDueDateForInput(dueDate, {
      includeRelative: false,
      isAllDay,
      ...options,
    })
  }

  if (startDate) {
    return formatDueDateForInput(startDate, {
      includeRelative: false,
      isAllDay,
      ...options,
    })
  }

  return 'Set due date'
}

function isMidnight(date: Date): boolean {
  return date.getHours() === 0 && date.getMinutes() === 0
}

/**
 * Get display info for a due date with color coding.
 * Matches moly-mobile's color conventions:
 * - Today → green
 * - Tomorrow → amber
 * - Overdue → red
 * - Future / no date → default
 */
export function getDueDateDisplayInfo(
  dueDate?: Date | null,
  startDate?: Date | null,
  isSomeday?: boolean,
  isAllDay?: boolean,
  options: LocaleOptions = {},
): { label: string; colorClass: string } {
  if (isSomeday) {
    return { label: 'Someday', colorClass: '' }
  }

  if (!dueDate && !startDate) {
    return { label: 'Set due date', colorClass: '' }
  }

  // Use the formatted range label for the text
  const label = formatDateRangeLabel(startDate, dueDate, isAllDay, options)

  if (!dueDate) {
    return { label, colorClass: '' }
  }

  return { label, colorClass: getDueDateColorClass(dueDate) }
}

/**
 * Canonical due-date emphasis colour, shared by every surface that renders a
 * due date (task list rows, board cards, cycles, task detail) so they stay
 * consistent. Mirrors moly-mobile:
 * - Today    → green
 * - Tomorrow → amber
 * - Overdue  → red
 * - Future / no date → muted
 */
export function getDueDateColorClass(due: Date | number | null | undefined): string {
  if (due == null) return 'text-muted-foreground'
  const date = typeof due === 'number' ? new Date(due) : due
  // Calm by default (design 6a): the accent is reserved for overdue.
  if (isToday(date)) return 'text-foreground'
  if (isTomorrow(date)) return 'text-secondary-foreground'
  if (isPast(startOfDay(date))) return 'text-brand'
  return 'text-muted-foreground'
}

/**
 * Compact age of an item for list columns (design 13b): "now", "5h", "3d",
 * "2w", "4mo", "1y". Rounds down; future timestamps read as "now".
 */
export function formatShortAge(from: number, now: number = Date.now()): string {
  const minutes = Math.floor((now - from) / 60000)
  if (minutes < 60) return 'now'
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 14) return `${days}d`
  if (days < 60) return `${Math.floor(days / 7)}w`
  if (days < 365) return `${Math.floor(days / 30)}mo`
  return `${Math.floor(days / 365)}y`
}
